import {
  Alignment,
  Button,
  Collapse,
  Icon,
  Radio,
  RadioGroup,
  Switch,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import {
  TEST_IDS,
  getPublishProjectDrawerCreationTypeTestId,
  getVirtualGraphItemTestId,
} from 'src/constants/testIds';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import {
  useFindVirtualGraph,
  useValidateVirtualGraphName,
  useVirtualGraphNamePlaceholder,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  CreationType,
  VirtualGraphMappingInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectSelector } from 'src/selectors/project';
import { virtualGraphItemsSelector } from 'src/selectors/virtualGraphItems';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type VirtualGraphMappingRowProps = {
  databaseId: string;
  info: VirtualGraphMappingInfo;
  initialInfo: VirtualGraphMappingInfo;
  mappings: Mapping[];
  setInfo: (info: VirtualGraphMappingInfo) => void;
  testId?: string;
  virtualGraph: ProjectVirtualGraph;
};

export const VirtualGraphMappingRow = ({
  databaseId,
  info,
  initialInfo,
  mappings,
  setInfo,
  testId,
  virtualGraph,
}: VirtualGraphMappingRowProps) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const didVirtualGraphChange = useDidValueChange(virtualGraph);
  const findVirtualGraph = useFindVirtualGraph();
  const validate = useValidateVirtualGraphName();
  const virtualGraphNamePlaceholder = useVirtualGraphNamePlaceholder(
    databaseId,
    virtualGraph
  );
  const virtualGraphSelectItems = useReduxSelector(
    virtualGraphItemsSelector(databaseId)
  );
  const connection = useReduxSelector(connectionSelector);
  const isCatalogEnabled = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );

  const [isOpen, setIsOpen] = React.useState(false);
  const [useAdvancedSettings, setUseAdvancedSettings] = React.useState(false);

  const initialExistingVirtualGraph =
    initialInfo.creationType === CreationType.NEW
      ? null
      : findVirtualGraph(info.virtualGraphName) || null;
  const [existing, setExisting] = React.useState<SelectItem | null>(
    initialExistingVirtualGraph
  );

  React.useEffect(() => {
    if (didVirtualGraphChange) {
      setExisting(initialExistingVirtualGraph);
      const expand = Boolean(
        initialInfo.creationType === CreationType.NEW &&
          validate(initialInfo.virtualGraphName)
      );

      if (expand) {
        setIsOpen(true);
        setUseAdvancedSettings(true);
      }
    }
  }, [
    didVirtualGraphChange,
    initialExistingVirtualGraph,
    initialInfo,
    project,
    validate,
    virtualGraph,
  ]);

  const handleSetInfo = React.useCallback(
    (nextInfo: Partial<VirtualGraphMappingInfo>) => {
      setInfo({ ...info, ...nextInfo });
    },
    [info, setInfo]
  );

  const { creationType, virtualGraphName } = info;
  const handleChangeUseAdvancedSettings = React.useCallback(
    ({ currentTarget: { checked } }: React.FormEvent<HTMLInputElement>) => {
      setUseAdvancedSettings(checked);
      if (!checked && creationType === CreationType.NEW) {
        handleSetInfo({ virtualGraphName: virtualGraphNamePlaceholder });
      }
    },
    [creationType, handleSetInfo, virtualGraphNamePlaceholder]
  );

  const handleSelectExisting = React.useCallback(
    (existing: SelectItem | null) => {
      setExisting(existing);
      if (existing?.value) {
        handleSetInfo({ virtualGraphName: existing.value });
      }
    },
    [handleSetInfo]
  );

  const handleChangeCreationType = React.useCallback(
    ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
      const newCreationType = value as CreationType;
      if (newCreationType === CreationType.NEW) {
        handleSetInfo({
          creationType: newCreationType,
          virtualGraphName: virtualGraphNamePlaceholder,
        });
        if (!virtualGraphNamePlaceholder) {
          setIsOpen(true);
          setUseAdvancedSettings(true);
        }
      } else if (newCreationType === CreationType.REPLACE) {
        handleSetInfo({
          creationType: newCreationType,
          virtualGraphName: existing?.value || '',
        });
      }
    },
    [existing, handleSetInfo, virtualGraphNamePlaceholder]
  );

  const isCatalogEnabledError: boolean =
    !isCatalogEnabled &&
    typeof virtualGraph.from !== 'string' &&
    Boolean(virtualGraph.from.catalog);

  return (
    <div className="sd-resource-mapping-row" data-testid={testId}>
      <div className="sd-resource-mapping-row__body">
        <div className="sd-resource-mapping-grid">
          <div className="sd-resource-mapping-grid__resource-name">
            <Text ellipsize>{virtualGraph.name}</Text>
            {isCatalogEnabledError ? (
              <Tooltip
                content={
                  copy.components.publishProjectDrawer.mappingConfiguration
                    .virtualGraph.versionWarning
                }
              >
                <Icon icon={IconNames.WARNING_SIGN} />
              </Tooltip>
            ) : null}
          </div>
          <div className="sd-resource-mapping-row__mappings">
            {mappings.map(({ key, text }) => (
              <Text key={key} ellipsize>
                {text}
              </Text>
            ))}
          </div>
          <RadioGroup
            inline
            onChange={handleChangeCreationType}
            selectedValue={creationType}
          >
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.NEW
              )}
              label={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.create
              }
              value={CreationType.NEW}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.REPLACE
              )}
              labelElement={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.replace
              }
              value={CreationType.REPLACE}
            />
            <div className="sd-drawer-selector-menu">
              <ItemSelector.Single
                buttonText={existing?.text || ''}
                disabled={creationType !== CreationType.REPLACE}
                filterInputProps={{
                  placeholder: copy.components.defaults.filter,
                }}
                items={virtualGraphSelectItems}
                itemTestId={getVirtualGraphItemTestId}
                onSelect={handleSelectExisting}
                selected={existing}
                testId={TEST_IDS.virtualGraph.selector}
              />
            </div>
          </RadioGroup>

          <div className="sd-resource-mapping-row__collapse-button">
            <Button
              data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseButton}
              icon={isOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
              minimal
              onClick={() => setIsOpen(!isOpen)}
            />
          </div>
        </div>
      </div>

      <Collapse isOpen={isOpen}>
        <div
          className="sd-resource-mapping-row__collapse-body"
          data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseBody}
        >
          <Switch
            alignIndicator={Alignment.RIGHT}
            checked={useAdvancedSettings}
            className="sd-drawer-use-advanced-settings"
            data-testid={TEST_IDS.publishProjectDrawer.useAdvancedSettings}
            inline
            label={
              copy.components.publishProjectDrawer.mappingConfiguration
                .advancedResourceSwitch
            }
            onChange={handleChangeUseAdvancedSettings}
          />

          {useAdvancedSettings ? (
            <InlineEditableLabel
              disabled={creationType === CreationType.REPLACE}
              label={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.label
              }
              onSetValue={(nextVirtualGraphName) =>
                handleSetInfo({ virtualGraphName: nextVirtualGraphName })
              }
              onValidate={
                creationType === CreationType.REPLACE ? () => '' : validate
              }
              placeholder={virtualGraphNamePlaceholder}
              testId={TEST_IDS.publishProjectDrawer.mapping.vgNameInput}
              value={
                creationType === CreationType.REPLACE && !existing
                  ? copy.components.publishProjectDrawer.mappingConfiguration
                      .virtualGraph.placeholder
                  : virtualGraphName
              }
            />
          ) : null}
        </div>
      </Collapse>
    </div>
  );
};
